import * as React from 'react';
import { WebAPI } from '../../MedicareProAPI/API/WebAPI';
import { UserPersonalToken } from '../../MedicareProAPI/Entities/User/UserPersonalToken';
import { UserPersonalTokenRepository } from '../../MedicareProAPI/Repositories/User/UserPersonalTokenRepository';
import StyledModal from '../Components/StyledModal';

export interface HealthSherpaMedicareEnrollButtonProps {
  userId: number;
  userAccessToken: string;
  indId: number;
  thisHealthSherpa?: boolean;
  stack?: boolean;
}

type Status = 'idle' | 'checking' | 'choice' | 'makingSession' | 'posting' | 'done' | 'error';

/**
 * Modal for interacting with HealthSherpaMedicare matrix session/ prospect post
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2025-10-07
 * @lastUpdated
 * @version 1.1.0
 */
export const HealthSherpaMedicareEnrollButton = (
  props: HealthSherpaMedicareEnrollButtonProps
): React.JSX.Element => {
  const userPersonalTokenRepository = React.useMemo(() => {
    const r = new UserPersonalTokenRepository();
    r.setAccessToken(props.userAccessToken);
    return r;
  }, [props.userAccessToken]);

  const webAPI = new WebAPI();
  webAPI.setAccessToken(props.userAccessToken);

  const [modalKey, setModalKey] = React.useState(0);
  const [modalOpen, setModalOpen] = React.useState<boolean>(false);

  const [healthSherpa, setHealthSherpa] = React.useState<boolean>(props.thisHealthSherpa || false);

  const [prospectPostUrl, setProspectPostUrl] = React.useState<string>('');

  const [status, setStatus] = React.useState<Status>('idle');
  const [error, setError] = React.useState<string | null>(null);

  const [showIntegrationInfo, setShowIntegrationInfo] = React.useState<boolean>(false);

  const ALL_CAN_POST_OPTIONS = [
    'Name',
    'DOB',
    'Email',
    'Phone',
    'Address',
    'MBI',
    'Medicaid',
    'Rx',
    'Pharmacies',
    'Providers',
  ];

  const canPostSet = new Set(['Name', 'DOB', 'Email', 'Phone', 'Address', 'MBI', 'Medicaid']);

  /**
   * Resets all SunFire enrollment/session UI state back to initial values.
   *
   * Side effects:
   * - Clears the selected SunFire account (`setUserSunFireSelected(undefined)`).
   * - Empties the current session id (`setSessionId('')`).
   * - Sets status back to `'idle'`.
   * - Clears any error (`setError(null)`).
   * - Clears the last session response (`setSessionResponse(null)`).
   * - Clears any generated prospect post URL (`setProspectPostUrl('')`).
   * - Disables the "use existing session" flag (`setUseExistingSession(false)`).
   *
   * @returns {void}
   * @example
   * // After closing the modal:
   * handleClose = () => {
   *   setModalOpen(false);
   *   reset();
   * };
   */
  const reset = React.useCallback((): void => {
    setStatus('idle');
    setError(null);
    setProspectPostUrl('');
  }, []);

  /**
   * Opens the enrollment modal. If exactly one SunFire account exists,
   * preselect it for the user.
   *
   * @returns {void}
   *
   * @example
   * // On button click
   * <button onClick={handleStartEnrollClick}>Start Enrollment</button>
   */
  const handleStartEnrollClick = () => {
    setModalOpen(true);
  };

  /**
   * Closes the modal, resets related state, and increments the modal key
   * to force an unmount/mount cycle (useful for clearing internal component state).
   *
   * Side effects:
   * - Calls `setModalOpen(false)` to hide the modal.
   * - Invokes `reset()` to clear enrollment/session state.
   * - Bumps `modalKey` via `setModalKey(k => k + 1)` to remount the modal subtree.
   *
   * @returns {void}
   *
   * @example
   * <StyledModal open={modalOpen} onClose={handleClose} />
   */
  const handleClose = () => {
    setModalOpen(false);
    reset();
    setModalKey((k) => k + 1);
  };

  /**
   * Creates a HealthSherpa Medicare prospect post and stores its redirect URL (if any).
   *
   * @returns {Promise<void>}
   */
  const makeProspectPost = async (): Promise<void> => {
    setStatus('posting');
    const payload: any = { indId: props.indId };

    try {
      const prospectPost = await webAPI.healthSherpaMedicareEnroll(props.userId, payload);

      if (prospectPost?.redirectUrl) {
        setProspectPostUrl(prospectPost.redirectUrl);
        setStatus('done');
      } else {
        setProspectPostUrl('');
        setError('Unable to generate redirect URL.');
      }
    } catch (e: any) {
      // Try to find "400" or "404" anywhere in the response or message
      const raw = JSON.stringify(e) || '';
      const text = e?.message || e?.toString() || raw;

      let status = 0;
      const match = text.match(/HTTP\/\d(?:\.\d)?\s(4\d{2})|[^0-9](4\d{2})[^0-9]/);
      if (match) {
        status = Number(match[1] || match[2]);
      }

      console.log('parsed vendor status:', status, text);

      if (status === 400) {
        setError(String(status));
      } else if (status === 403) {
        setError(String(status));
      } else if (status === 404) {
        setError(String(status));
      } else {
        setError('Unable to generate redirect URL.');
      }
      setStatus('error');
    }
  };

  /**
   * When the modal opens with valid user HSFM connection, kick off enrollment.
   *
   * Triggers:
   * - Runs whenever `modalOpen` or `healthSherpa` changes.
   * - If the modal isn’t open or no account is selected, it exits early.
   *
   * Side effects:
   * - Invokes `startEnroll()` which manages status and posting flow.
   */
  React.useEffect(() => {
    if (!modalOpen || !healthSherpa) return;
    makeProspectPost();
  }, [modalOpen, healthSherpa]);

  return (
    <>
      {healthSherpa && !props.stack && (
        <div
          className="btn btn-sm btn-white mb-3 mr-2 float-right"
          onClick={handleStartEnrollClick}
        >
          <img
            style={{ height: '20px' }}
            className="mr-1"
            alt="HealthSherpa Medicare"
            src="https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com/public/img/site/healthsherpamedicareicon.png"
          />
          <b>Enroll</b>
        </div>
      )}

      {healthSherpa && props.stack && (
        <div
          className={`btn border-primary text-primary btn-sm p-2 mt-2 d-block mx-auto cp px-3 hover-shadow `}
          style={{ borderRadius: '15px' }}
          onClick={handleStartEnrollClick}
        >
          <img
            className="mr-2"
            style={{ height: '20px' }}
            alt="HealthSherpa Medicare"
            src="https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com/public/img/site/healthsherpamedicareicon.png"
          />
          <b>HealthSherpa Medicare</b>
        </div>
      )}
      {modalOpen && (
        <StyledModal
          key={modalKey}
          size="lg"
          title="HealthSherpa Medicare"
          footerCloseButton
          show={true}
          onClose={handleClose}
        >
          {healthSherpa && (
            <div className="row">
              {/* left column */}
              <div className="col-12 col-lg-8 text-center align-self-center">
                {status === 'posting' && (
                  <div>
                    <b>
                      <i className="far fa-spinner fa-spin text-primary mr-2" />
                      Sending to HealthSherpa Medicare...
                    </b>
                  </div>
                )}

                {status === 'done' && (
                  <>
                    <a
                      className={`btn border-primary text-primary btn-sm p-2 d-inline-block mx-auto cp px-3 hover-shadow ${!prospectPostUrl ? 'disabled' : ''}`}
                      href={prospectPostUrl ?? '#'}
                      style={{ borderRadius: '15px' }}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        if (!prospectPostUrl) e.preventDefault();
                      }}
                      aria-disabled={!prospectPostUrl}
                    >
                      <img
                        className="mr-2"
                        style={{ height: '20px' }}
                        alt="HealthSherpa Medicare"
                        src="https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com/public/img/site/healthsherpamedicareicon.png"
                      />
                      <b>Continue to HealthSherpa Medicare</b>{' '}
                      <i className="far fa-arrow-alt-circle-right ml-2"></i>
                    </a>
                  </>
                )}

                {status === 'error' && (
                  <div className="mt-2 rounded bg-contrast p-3 small text-left">
                    <div className="text-danger mb-2" aria-hidden="true">
                      <b>
                        <i className="far fa-exclamation-circle mr-2"></i>We couldn’t send data to
                        HealthSherpa Medicare.
                      </b>
                    </div>
                    {error === '400' && (
                      <>
                        There is something wrong with the <b>data</b> we sent HSFM. Double-check
                        required field: <b>first name, last name</b> and try again.
                      </>
                    )}
                    {error === '403' && (
                      <>
                        The HSFM <b>API Key</b> you are using is invalid or does not match your
                        account.
                      </>
                    )}
                    {error === '404' && (
                      <>
                        The HSFM <b>username</b> you saved for this connection is invalid. If your
                        agency manages your connection in MPCRM, please contact them. If you manage
                        your own connection, open{' '}
                        <a href="https://www.medicareproapp.com/agent/settings/index/integration">
                          Settings
                        </a>{' '}
                        and verify your username.
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* right column (sibling, not nested inside col-8) */}
              <div className="col-12 col-lg-4">
                <>
                  <div className="mb-2">
                    What we can send to <b>HealthSherpa Medicare</b>
                  </div>
                  {ALL_CAN_POST_OPTIONS.map((opt) => {
                    const allowed = canPostSet.has(String(opt));
                    return (
                      <span
                        key={opt}
                        className={`badge dataBadge text-dark mr-2 cp ${allowed ? 'table-success' : 'table-danger'}`}
                        title={allowed ? 'Included' : 'Not included'}
                        onClick={() => setShowIntegrationInfo(!showIntegrationInfo)}
                      >
                        <i className={`mr-1 far ${allowed ? 'fa-check' : 'fa-times'}`}></i>
                        {opt}
                      </span>
                    );
                  })}

                  <div className="mb-2 mt-4">
                    What HealthSherpa Medicare <b>sends back</b> after an <b>enrollment</b>
                  </div>

                  <span
                    key={'no-sync-sunfire'}
                    onClick={() => setShowIntegrationInfo(!showIntegrationInfo)}
                    className="badge table-danger text-dark dataBadge mr-2 cp"
                  >
                    no sync
                  </span>

                  <div className="mt-2">
                    <div
                      className="badge border-secondary cp"
                      onClick={() => setShowIntegrationInfo(!showIntegrationInfo)}
                    >
                      <i className="fas fa-question-circle mr-1"></i>How this works
                    </div>
                  </div>
                </>
              </div>
              {showIntegrationInfo && (
                <div className="col-12 mt-2">
                  <div className="mt-2 rounded bg-contrast p-3 small">
                    <div className="mb-2">
                      <b>How the integration works</b>
                    </div>
                    <ul>
                      <li>
                        In the current phase, MPCRM can only send basic application data over to
                        HSFM.
                      </li>
                      <li>
                        If you have already sent this profile to HSFM we will <b>update</b> the
                        record when you click continue
                      </li>
                      <li>HSFM does not currently send enrollment data back to MPCRM.</li>
                    </ul>
                    <div
                      className="badge badge-secondary cp"
                      onClick={() => setShowIntegrationInfo(!showIntegrationInfo)}
                    >
                      Close
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </StyledModal>
      )}
    </>
  );
};
